import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { WorkspaceService } from '@/api/generated/services/WorkspaceService'
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse'
import { useAppSelector } from '@/store/hooks'

interface WorkspaceSelectProps {
  selectedWorkspaceId: string
  selectedWorkspaceName: string
  onSelect: (workspaceId: string, workspaceName: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  showAllOption?: boolean
}

const WorkspaceSelect: React.FC<WorkspaceSelectProps> = ({
  selectedWorkspaceId,
  selectedWorkspaceName,
  onSelect,
  disabled = false,
  error = null,
  placeholder = '워크스페이스를 선택하세요',
  showAllOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [workspaces, setWorkspaces] = useState<WorkspaceResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Fetch workspaces
  const fetchWorkspaces = useCallback(async () => {
    if (!selectedWorkspace?.org_id) return

    setIsLoading(true)
    setFetchError(null)

    try {
      const response = await WorkspaceService.appsWorkspaceViewsListWorkspaces({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: 1000
      })

      if (response?.data) {
        setWorkspaces(response.data as WorkspaceResponse[])
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err)
      setFetchError('워크스페이스 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [selectedWorkspace?.org_id])

  useEffect(() => {
    fetchWorkspaces()
  }, [fetchWorkspaces])

  // Filter workspaces based on search term
  const filteredWorkspaces = useMemo(() => {
    if (!searchTerm.trim()) return workspaces
    const searchLower = searchTerm.toLowerCase().trim()
    return workspaces.filter(ws =>
      ws.name.toLowerCase().includes(searchLower)
    )
  }, [workspaces, searchTerm])

  const handleSelect = (workspace: WorkspaceResponse) => {
    onSelect(workspace.id, workspace.name)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('', '')
    setSearchTerm('')
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('')
      }
    }
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: selectedWorkspaceName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span className="truncate">{selectedWorkspaceName || placeholder}</span>
          <div className="flex items-center gap-2">
            {selectedWorkspaceId && selectedWorkspaceId.trim() !== '' && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
            <div className="relative">
              <Search
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: designTokens.colors.gray[3] }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="워크스페이스 검색..."
                className="w-full pl-2 pr-3 py-2 border rounded"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6]
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Dropdown Content */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                워크스페이스 목록을 불러오는 중...
              </div>
            ) : fetchError ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.error.DEFAULT
                }}
              >
                {fetchError}
              </div>
            ) : workspaces.length === 0 ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                워크스페이스가 없습니다.
              </div>
            ) : filteredWorkspaces.length === 0 ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                검색 결과가 없습니다.
              </div>
            ) : (
              <>
                {/* All Option */}
                {showAllOption && !searchTerm.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      onSelect('', '')
                      setIsOpen(false)
                      setSearchTerm('')
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    전체
                  </button>
                )}

                {filteredWorkspaces.map((workspace) => (
                  <button
                    key={workspace.id}
                    type="button"
                    onClick={() => handleSelect(workspace)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {workspace.name}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="mt-1 text-sm"
          style={{
            color: designTokens.colors.error.DEFAULT,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default WorkspaceSelect
